/**
 * API utilities for 10 Thirty web app.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.10thirty.co.za';

/**
 * Read the CSRF token from the tt_csrf cookie.
 */
function getCSRFToken() {
  const match = document.cookie.match(/tt_csrf=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Make an API request with credentials (cookies).
 * Automatically includes CSRF token header on POST/PUT/DELETE requests.
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Include CSRF token on mutation requests
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Send cookies
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Check current session status.
 */
export async function checkSession() {
  return apiRequest('/api/web/session');
}

/**
 * Validate magic link token and establish session.
 */
export async function validateToken(token) {
  return apiRequest('/api/web/session/validate', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Log out of web session.
 */
export async function logout() {
  return apiRequest('/api/web/session/logout', {
    method: 'POST',
  });
}

/**
 * Get full profile data.
 */
export async function getProfile() {
  return apiRequest('/api/web/profile');
}

/**
 * Cancel subscription.
 */
export async function cancelSubscription() {
  return apiRequest('/api/web/profile/cancel-subscription', {
    method: 'POST',
  });
}

/**
 * Get available plans.
 */
export async function getPlans() {
  return apiRequest('/api/subscriptions/plans');
}

/**
 * Create checkout session for subscription (uses web cookie auth).
 */
export async function createSubscriptionCheckout(plan, seatCount = 1) {
  return apiRequest('/api/web/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      plan,
      seat_count: seatCount,
      return_url: `${window.location.origin}/profile?payment=success`,
      cancel_url: `${window.location.origin}/profile?payment=cancelled`,
    }),
  });
}

/**
 * Create checkout session for a tunnel pack purchase (uses web cookie auth).
 */
export async function purchaseTunnelPack(packSize) {
  return apiRequest('/api/web/tunnel-packs/purchase', {
    method: 'POST',
    body: JSON.stringify({
      pack_size: packSize,
      return_url: `${window.location.origin}/profile?payment=success`,
      cancel_url: `${window.location.origin}/profile?payment=cancelled`,
    }),
  });
}

/**
 * Verify payment status after redirect.
 */
export async function verifyPayment(merchantTransactionId) {
  return apiRequest(`/api/web/payments/verify/${merchantTransactionId}`);
}

/**
 * Delete an incomplete (pending/failed) payment.
 */
export async function deletePayment(paymentId) {
  return apiRequest(`/api/web/payments/${paymentId}`, {
    method: 'DELETE',
  });
}

/**
 * Retry an incomplete payment — returns a new checkout URL.
 */
export async function retryPayment(paymentId) {
  return apiRequest(`/api/web/payments/${paymentId}/retry`, {
    method: 'POST',
  });
}

// ============= Partner program (user-facing) =============

/**
 * Get the current user's partner program standing — tier, rate, count, totals.
 */
export async function getPartnerSummary() {
  return apiRequest('/api/referrals/partner');
}

// ============= Business Seats =============

/**
 * Add a business team seat.
 */
export async function addSeat(phone, plan) {
  return apiRequest('/api/web/seats', {
    method: 'POST',
    body: JSON.stringify({ phone, plan }),
  });
}

/**
 * Update a seat's assigned plan.
 */
export async function updateSeat(seatId, plan) {
  return apiRequest(`/api/web/seats/${seatId}`, {
    method: 'PUT',
    body: JSON.stringify({ plan }),
  });
}

/**
 * Remove a business team seat.
 */
export async function removeSeat(seatId) {
  return apiRequest(`/api/web/seats/${seatId}`, {
    method: 'DELETE',
  });
}
